import { Injectable } from '@angular/core';
import { HttpStatusCode } from '@angular/common/http';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { isApiError } from '@nx-ng/util';
import { environment } from '../../environments/environment';
import { LoggingService, LogLevel } from '../types';

@Injectable({
  providedIn: 'root',
})
export class ApplicationInsightsService implements LoggingService {
  appInsights: ApplicationInsights;
  constructor() {
    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: environment.appInsights.key,
        enableAutoRouteTracking: true, // log all route changes
      },
    });
    this.appInsights.loadAppInsights();
    this.appInsights.trackPageView();
  }

  // Example methods from MS example
  logPageView(name?: string, url?: string) {
    this.appInsights.trackPageView({
      name: name,
      uri: url,
    });
  }
  logEvent(name: string, properties?: { [key: string]: unknown }) {
    this.appInsights.trackEvent(
      { name: name },
      this.annotate('info', properties)
    );
  }
  logMetric(
    name: string,
    average: number,
    properties?: { [key: string]: unknown }
  ) {
    this.appInsights.trackMetric(
      { name: name, average: average },
      this.annotate('info', properties)
    );
  }

  // Conventional logging levels
  logDebug(message: string, properties?: { [key: string]: unknown }) {
    this.appInsights.trackTrace(
      { message: message },
      this.annotate('debug', properties)
    );
  }
  logInfo(message: string, properties?: { [key: string]: unknown }) {
    this.appInsights.trackTrace(
      { message: message },
      this.annotate('info', properties)
    );
  }
  logWarning(message: string, properties?: { [key: string]: unknown }) {
    this.appInsights.trackTrace(
      { message: message },
      this.annotate('warning', properties)
    );
  }
  logError(exception: Error, properties?: { [key: string]: unknown }) {
    this.appInsights.trackException({
      exception: exception,
      properties: this.annotate('error', properties),
    });
  }

  logApiError(properties?: { [key: string]: unknown }, stack?: string) {
    const isBadRequest =
      properties &&
      isApiError(properties.apiError) &&
      properties.apiError.code === HttpStatusCode.BadRequest;

    this.logError(
      {
        name: isBadRequest ? 'API Error' : 'API Bad Request',
        message: 'An API error occurred',
        stack: stack,
      },
      properties
    );
  }

  private annotate(
    level: LogLevel,
    properties: { [key: string]: unknown } | undefined
  ): { [key: string]: unknown } | undefined {
    const reply = properties ?? {};
    reply['logging-namespace'] = 'nx-bus-e-commerce-spa';
    reply['logging-level'] = level;
    return properties;
  }
}
